import type {ReactNode} from 'react'
import type React from 'react'
import {Sidepanel} from 'pepsico-ds'
import type {SidepanelIProps} from 'pepsico-ds'

/**
 * Extended props for the SidePanel component
 * Includes the original Sidepanel props plus commonly used side panel props
 * for better developer experience
 */
export interface SidePanelProps extends Omit<SidepanelIProps, 'title'> {
  /**
   * Title of the panel (required by the original component)
   */
  title: string
  /**
   * Alternative to showSidePanel - controls whether the side panel is visible
   */
  isOpen?: boolean

  /**
   * Alternative to onCloseSidePanel - callback when the panel is closed
   */
  onClose?: () => void

  /**
   * Width of the panel (maps to horizontalSidepanelWidth for horizontal panels)
   */
  width?: string

  /**
   * Position of the panel ('left', 'right', 'top', 'bottom')
   */
  position?: 'left' | 'right' | 'top' | 'bottom'

  /**
   * Title displayed in the header of the side panel
   */
  header?: string

  /**
   * Whether to show the close icon in the header
   */
  showCloseIcon?: boolean

  /**
   * Whether to show the footer of the side panel
   * @default false
   */
  showFooter?: boolean

  /**
   * Whether the side panel should close when clicking outside
   * @default true
   */
  closeOnOutsideClick?: boolean

  /**
   * Content to render inside the side panel
   */
  children?: ReactNode
}

/**
 * SidePanel is a wrapper component for the Sidepanel component from pepsico-ds
 * It provides a simplified and more intuitive API while maintaining all original functionality
 */
const SidePanel: React.FC<SidePanelProps> = ({
  // Handle both naming conventions for open/close
  isOpen,
  showSidePanel,
  onClose,
  onCloseSidePanel,
  width,
  horizontalSidepanelWidth,
  showFooter = false, // Set default value to false
  title,
  // Other standard props
  children,
  // position,
  ...rest
}) => {
  // Use isOpen as a fallback if showSidePanel is not provided
  const isPanelOpen = showSidePanel ?? isOpen

  // Use onCloseSidePanel as a fallback if onClose is not provided
  const handleClose = onCloseSidePanel ?? onClose

  // Use width as a fallback if horizontalSidepanelWidth is not provided
  const panelWidth = horizontalSidepanelWidth ?? width

  // Make sure we have either isOpen or showSidePanel defined
  if (isPanelOpen === undefined) {
    console.warn('SidePanel: Either isOpen or showSidePanel prop should be provided')
  }

  // Make sure we have either onClose or onCloseSidePanel defined
  if (!handleClose) {
    console.warn('SidePanel: Either onClose or onCloseSidePanel prop should be provided')
  }

  return (
    <Sidepanel
      showSidePanel={isPanelOpen}
      onCloseSidePanel={handleClose}
      horizontalSidepanelWidth={panelWidth}
      title={title}
      showFooter={showFooter}
      className="select-none" // Prevent text selection cursor
      {...rest}
    >
      {children}
    </Sidepanel>
  )
}

export default SidePanel
